import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('trxl_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('trxl_token');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/password-reset-request', null, { params: { email } }),
  resetPassword: (token, newPassword) => api.post('/auth/password-reset', null, { params: { token, new_password: newPassword } }),
};

// Files API
export const filesAPI = {
  list: (params) => api.get('/files/', { params }),
  get: (id) => api.get(`/files/${id}`),
  upload: (formData) => api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  addComment: (id, message) => api.post(`/files/${id}/comment`, null, { params: { message } }),
  requestRevision: (id, reason) => api.post(`/files/${id}/revision`, null, { params: { reason } }),
  download: (id, fileType = 'tuned') => api.get(`/files/${id}/download`, { 
    params: { file_type: fileType },
    responseType: 'blob',
  }),
  getHexPreview: (id, offset = 0, length = 512) => api.get(`/files/${id}/hex-preview`, { params: { offset, length } }),
  getTeamNotes: (id) => api.get(`/files/${id}/team-notes`),
  addTeamNote: (id, data) => api.post(`/files/${id}/team-notes`, data),
  deleteTeamNote: (fileId, noteId) => api.delete(`/files/${fileId}/team-notes/${noteId}`),
  // Versioning & Timeline
  getTimeline: (id) => api.get(`/files/${id}/timeline`),
  createVersion: (id, formData) => api.post(`/files/${id}/versions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addVersionAttachment: (fileId, versionId, formData) => api.post(`/files/${fileId}/versions/${versionId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadVersionAttachment: (fileId, versionId, attType) => api.get(`/files/${fileId}/versions/${versionId}/download/${attType}`, { responseType: 'blob' }),
  // Reclamations
  createReclamation: (id, formData) => api.post(`/files/${id}/reclamation`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  resolveReclamation: (fileId, recId, formData) => api.put(`/files/${fileId}/reclamation/${recId}/resolve`, formData),
  // File Comparison (B5)
  compareFiles: (formData) => api.post('/files/compare', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Virtual Dyno (B8)
  calculateDyno: (formData) => api.post('/files/dyno/calculate', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  evaluateLog: (formData) => api.post('/files/dyno/evaluate', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  compareDyno: (formData) => api.post('/files/dyno/compare', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  aiEvaluateLog: (formData) => api.post('/files/dyno/ai-evaluate', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
};

// Wallet API
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  getTransactions: (params) => api.get('/wallet/transactions', { params }),
  createTopup: (amount, paymentMethod) => api.post('/wallet/topup', { amount, payment_method: paymentMethod }),
  checkPaymentStatus: (sessionId) => api.get(`/wallet/payment-status/${sessionId}`),
  capturePayPal: (orderId) => api.post(`/wallet/paypal/capture/${orderId}`),
  getPaymentMethods: () => api.get('/wallet/payment-methods'),
  getCreditPackages: () => api.get('/wallet/credit-packages'),
};

// Invoices API
export const invoicesAPI = {
  list: (params) => api.get('/invoices/', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// DTC API
export const dtcAPI = {
  search: (params) => api.get('/dtc/search', { params }),
  get: (code) => api.get(`/dtc/${code}`),
  suggestions: (ecuType) => api.get('/dtc/suggestions/by-ecu', { params: { ecu_type: ecuType } }),
};

// Bosch API
export const boschAPI = {
  search: (params) => api.get('/bosch/search', { params }),
  get: (partNumber) => api.get(`/bosch/${partNumber}`),
};

// Knowledge Base API
export const kbAPI = {
  listArticles: (params) => api.get('/kb/articles', { params }),
  getArticle: (slug) => api.get(`/kb/articles/${slug}`),
  getCategories: () => api.get('/kb/categories'),
};

// User API
export const userAPI = {
  updateProfile: (data) => api.put('/users/profile', data),
  getProfile: () => api.get('/users/profile'),
  changePassword: (data) => api.post('/users/change-password', data),
  getNotifications: (unreadOnly = false) => api.get('/users/notifications', { params: { unread_only: unreadOnly } }),
  markNotificationRead: (id) => api.put(`/users/notifications/${id}/read`),
};

// Admin API
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, null, { params: data }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id, newPassword) => api.post(`/admin/users/${id}/reset-password`, null, { params: { new_password: newPassword } }),
  getFiles: (params) => api.get('/admin/files', { params }),
  updateFileStatus: (id, status) => api.put(`/admin/files/${id}/status`, null, { params: { status } }),
  uploadTunedFile: (id, formData) => api.post(`/admin/files/${id}/upload-tuned`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStats: () => api.get('/admin/stats'),
  getServiceSchedule: () => api.get('/admin/service-schedule'),
  updateServiceSchedule: (schedule) => api.put('/admin/service-schedule', schedule),
  getShopOrders: (params) => api.get('/admin/shop-orders', { params }),
  getShopOrder: (id) => api.get(`/admin/shop-orders/${id}`),
  updateShopOrderStatus: (id, status, tracking) => api.put(`/admin/shop-orders/${id}/status`, null, { params: { status, tracking_number: tracking } }),
  createOrderInvoice: (id) => api.post(`/admin/shop-orders/${id}/invoice`),
  updateOrderTracking: (id, trackingNumber, carrier) => api.put(`/admin/shop-orders/${id}/tracking`, null, { params: { tracking_number: trackingNumber, carrier } }),
  adjustCredits: (userId, amount, reason) => api.post(`/admin/users/${userId}/credits`, { user_id: userId, amount, reason }),
  // Credit packages
  getCreditPackages: () => api.get('/admin/credit-packages'),
  createCreditPackage: (data) => api.post('/admin/credit-packages', data),
  updateCreditPackage: (id, data) => api.put(`/admin/credit-packages/${id}`, data),
  deleteCreditPackage: (id) => api.delete(`/admin/credit-packages/${id}`),
  // Tuning option pricing
  getTuningOptions: () => api.get('/admin/tuning-options'),
  updateTuningOption: (id, data) => api.put(`/admin/tuning-options/${id}`, data),
  createTuningOption: (data) => api.post('/admin/tuning-options', data),
  deleteTuningOption: (id) => api.delete(`/admin/tuning-options/${id}`),
  deleteOrder: (id) => api.delete(`/admin/shop-orders/${id}`),
  deleteFile: (id) => api.delete(`/admin/files/${id}`),
  deleteTicket: (id) => api.delete(`/admin/tickets/${id}`),
  // Credit price
  getCreditPrice: () => api.get('/admin/credit-price'),
  updateCreditPrice: (price) => api.put('/admin/credit-price', null, { params: { price } }),
  // Support tickets
  getTickets: (params) => api.get('/admin/tickets', { params }),
  getTicket: (id) => api.get(`/admin/tickets/${id}`),
  replyTicket: (id, message) => api.post(`/admin/tickets/${id}/reply`, { message }),
  updateTicketStatus: (id, status) => api.put(`/admin/tickets/${id}/status`, null, { params: { status } }),
  // Scraper
  runScraper: (sources) => api.post('/admin/scraper/run', null, { params: { sources } }),
  getScraperStatus: () => api.get('/admin/scraper/status'),
  getScraperRuns: () => api.get('/admin/scraper/runs'),
  getScrapedVehicles: (params) => api.get('/admin/scraper/vehicles', { params }),
  getScraperSchedule: () => api.get('/admin/scraper/schedule'),
  setScraperSchedule: (enabled) => api.post('/admin/scraper/schedule', null, { params: { enabled } }),
  uploadCSV: (file, source = 'magicmotorsport') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    return api.post('/admin/scraper/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Chart data
  getChartData: () => api.get('/admin/chart-data'),
  // Monitoring
  getMonitoring: () => api.get('/admin/monitoring'),
  getActivityFeed: (limit = 20) => api.get('/admin/activity-feed', { params: { limit } }),
  // Email Notifications
  getEmailTemplates: () => api.get('/admin/email-templates'),
  saveEmailTemplate: (name, subject, body) => api.post('/admin/email-templates', null, { params: { name, subject, body } }),
  deleteEmailTemplate: (id) => api.delete(`/admin/email-templates/${id}`),
  sendNotification: (subject, body, recipients) => api.post('/admin/send-notification', null, { params: { subject, body, recipients } }),
  getNotificationHistory: (limit = 20) => api.get('/admin/notification-history', { params: { limit } }),
  getRecipientCounts: () => api.get('/admin/notification-recipients-count'),
  // Chat toggle
  getChatStatus: () => api.get('/admin/chat-status'),
  toggleChat: (enabled) => api.put('/admin/chat-toggle', null, { params: { enabled } }),
  getPaymentMethodsStatus: () => api.get('/admin/payment-methods-status'),
  togglePaymentMethod: (method, enabled) => api.put('/admin/payment-methods-toggle', null, { params: { method, enabled } }),
  // Quality Analytics (B7)
  getAnalyticsOverview: () => api.get('/admin/analytics/overview'),
  getReclamationByTuner: () => api.get('/admin/analytics/reclamation-by-tuner'),
  getReclamationByEcu: () => api.get('/admin/analytics/reclamation-by-ecu'),
  getReclamationByOption: () => api.get('/admin/analytics/reclamation-by-option'),
  getRevenueByBrand: () => api.get('/admin/analytics/revenue-by-brand'),
  getAnalyticsWarnings: () => api.get('/admin/analytics/warnings'),
  // Buchhaltung (fs-patch-v3)
  listInvoices: (params) => api.get('/admin/invoices/list', { params }),
  cancelInvoice: (invoiceId) => api.post(`/admin/invoices/${invoiceId}/cancel`),
  invoicePdfUrl: (invoiceId) => `${API_URL}/api/admin/invoices/${invoiceId}/pdf`,
  correctBalance: (userId, newBalance, reason) =>
    api.post(`/admin/wallet/correct-balance/${userId}`, { new_balance: newBalance, reason }),
  runBackfill: (dryRun = true, userId = null) =>
    api.post('/admin/wallet/backfill', { dry_run: dryRun, user_id: userId }),
  walletHistory: (params) => api.get('/admin/wallet/history', { params }),
};

// File Database API (B1 - File Matching)
export const fileDatabaseAPI = {
  getStats: () => api.get('/file-database/stats'),
  matchFile: (fileId, params) => api.get(`/file-database/match/${fileId}`, { params }),
  search: (params) => api.get('/file-database/search', { params }),
  getFolderMap: () => api.get('/file-database/folder-map'),
  updateFolderMap: (mapping) => api.put('/file-database/folder-map', mapping),
  analyzeAndMatch: (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/file-database/analyze-and-match', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
  },
};

// Chat API
export const chatAPI = {
  getStatus: () => api.get('/chat/status'),
  getConversations: () => api.get('/chat/conversations'),
  createConversation: (participantId, message) => api.post('/chat/conversations', { participant_id: participantId, message }),
  getMessages: (convId, limit = 50) => api.get(`/chat/conversations/${convId}/messages`, { params: { limit } }),
  sendMessage: (convId, message) => api.post(`/chat/conversations/${convId}/messages`, { message }),
  markRead: (convId) => api.put(`/chat/conversations/${convId}/read`),
  getUnreadCount: () => api.get('/chat/unread-count'),
  getUsers: (search) => api.get('/chat/users', { params: { search } }),
};

// Support Tickets API
export const ticketsAPI = {
  create: (data) => api.post('/tickets/', data),
  list: () => api.get('/tickets/'),
  get: (id) => api.get(`/tickets/${id}`),
  reply: (id, message) => api.post(`/tickets/${id}/reply`, { message }),
};

// Dyno Diagrams API
export const dynoAPI = {
  upload: (fileId, formData) => api.post(`/files/${fileId}/dyno-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list: (fileId) => api.get(`/files/${fileId}/dyno-diagrams`),
  download: (fileId, diagramId) => api.get(`/files/${fileId}/dyno-diagrams/${diagramId}/download`, { responseType: 'blob' }),
};

// Tuning API
export const tuningAPI = {
  getOptions: () => api.get('/tuning-options'),
  analyzeFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analyze-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Shop API
export const shopAPI = {
  getProducts: () => api.get('/shop/products'),
  getProduct: (id) => api.get(`/shop/products/${id}`),
  createOrder: (data) => api.post('/shop/orders', data),
  getOrders: () => api.get('/shop/orders'),
  getOrder: (id) => api.get(`/shop/orders/${id}`),
};

// Vehicles API
export const vehiclesAPI = {
  getBrands: () => api.get('/vehicles/brands'),
  getModels: (brand) => api.get(`/vehicles/models/${brand}`),
  getVersions: (brand, model) => api.get(`/vehicles/versions/${brand}/${model}`),
  getEcuInfo: (brand, model) => api.get(`/vehicles/ecu-info/${brand}/${model}`),
  search: (params) => api.get('/vehicles/search', { params }),
};

// Public API
export const publicAPI = {
  getServiceStatus: () => api.get('/service-status'),
  getLocales: () => api.get('/i18n/locales'),
  getTranslations: (locale) => api.get(`/i18n/translations/${locale}`),
};

export default api;
